import { type QConfigTableData } from '@exam-management/common/types';
import { useQConfig } from '@exam-management/feature-qconfig-provider';
import { QConfigStructureCategory } from '@exam-management/graphql';
import { useMemo } from 'react';

import { removeStructuresAndItsChildrenByQuestionType } from '../../utils';
import { useQuestionConditionsData } from '../use-question-conditions-data';

interface UseGetSingleQuestionConditionsDataInput {
  chapterId: string;
  questionTypeRow?: QConfigTableData;
}

export function useGetSingleQuestionConditionsData(
  input: Readonly<UseGetSingleQuestionConditionsDataInput>,
) {
  const { chapterId, questionTypeRow } = input;
  const {
    qConfigData: { structures: rawStructures },
  } = useQConfig();

  const structures = useMemo(() => {
    return removeStructuresAndItsChildrenByQuestionType({
      structures: rawStructures,
      removeTargetCategory: QConfigStructureCategory.ItemSet,
    });
  }, [rawStructures]);

  const chapterData = structures.find((structure) => structure.id === chapterId);

  if (!chapterData) {
    throw new Error('Chapter data not found');
  }

  const { levelItemsWithLevel, isLoading, checkExistOnStructures } = useQuestionConditionsData({
    chapterData,
    structures,
    questionTypeRow,
  });

  const defaultLOSRefId = useMemo(() => {
    return (
      structures.find(
        (row) =>
          row.category === QConfigStructureCategory.LearningObjectiveStatement &&
          row.parentId === questionTypeRow?.id,
      )?.refId ?? undefined
    );
  }, [structures, questionTypeRow]);

  return {
    structures,
    chapterData,
    levelItemsWithLevel,
    defaultLOS: defaultLOSRefId,
    checkExistOnStructures,
    isLoading,
  };
}
